import {
    ScheduleLifecycleVerb,
    ScheduleSourceType,
} from "@common/types/domains/accounting/schedule";
import { BulkScheduleRow } from "../bulk/bulkScheduleRows";
import { ScheduleApplicationParentRow } from "./scheduleApplicationRowHelpers";
import { getScheduleRowApplicationId } from "./scheduleApplicationRowId";
import { getAvailableScheduleLifecycleVerbs } from "./scheduleApplicationStatusDisplay";
import {
    SCHEDULE_CHANGE_POLICY_DISABLED_REASONS,
    SCHEDULE_CHANGE_POLICY_SKIP_REASONS,
    ScheduleBulkSkippedRow,
} from "./scheduleLifecycleBulkCopy";

type PolicyChangeRow = Pick<
    ScheduleApplicationParentRow,
    | "id"
    | "scheduleApplicationId"
    | "entityId"
    | "templateId"
    | "sourceType"
    | "status"
    | "isMilestone"
    | "isLineScoped"
    | "applicationAmount"
    | "matchTarget"
    | "sourceLabel"
>;

export interface SchedulePolicyChangePartition {
    rows: BulkScheduleRow[];
    /** Selected rows that can't move policy; listed in the confirm, not blocking. */
    skipped: ScheduleBulkSkippedRow[];
    /** Set only when no selected row can move policy. */
    disabledReason: string | undefined;
}

function getPolicyChangeBlockReason(row: PolicyChangeRow): string | undefined {
    if (row.isMilestone) {
        return SCHEDULE_CHANGE_POLICY_SKIP_REASONS.milestone;
    }
    if (row.isLineScoped) {
        return SCHEDULE_CHANGE_POLICY_SKIP_REASONS.lineItem;
    }
    if (
        !getAvailableScheduleLifecycleVerbs(row).includes(
            ScheduleLifecycleVerb.change,
        )
    ) {
        return SCHEDULE_CHANGE_POLICY_SKIP_REASONS.ended;
    }
    switch (row.sourceType) {
        case ScheduleSourceType.bill:
            return SCHEDULE_CHANGE_POLICY_SKIP_REASONS.bill;
        case ScheduleSourceType.manual:
            return SCHEDULE_CHANGE_POLICY_SKIP_REASONS.manual;
        case ScheduleSourceType.transaction:
        case ScheduleSourceType.invoice:
            return undefined;
    }
}

function toBulkScheduleRow(row: PolicyChangeRow): BulkScheduleRow | null {
    const target = row.matchTarget;
    const base = {
        entityId: row.entityId,
        existingApplicationIds: [getScheduleRowApplicationId(row)],
        existingTemplateId: row.templateId,
        hasMatchedSchedule: false,
        blockReason: null,
        transactionAmount: null,
    };
    if (target && "transactionId" in target) {
        return {
            ...base,
            source: {
                sourceType: ScheduleSourceType.transaction,
                transactionId: target.transactionId,
                amount: Math.abs(row.applicationAmount),
            },
        };
    }
    if (target && target.documentType === "invoice") {
        return {
            ...base,
            source: {
                sourceType: ScheduleSourceType.invoice,
                invoiceId: target.documentId,
                amount: Math.abs(row.applicationAmount),
            },
        };
    }
    return null;
}

export function getScheduleChangePolicyListReason(options: {
    selectedCount: number;
    optionCount: number;
    isLoadingTemplates: boolean;
    hasTemplatesError: boolean;
}): string | undefined {
    if (options.selectedCount === 0 || options.optionCount > 0) {
        return undefined;
    }
    if (options.isLoadingTemplates) {
        return SCHEDULE_CHANGE_POLICY_DISABLED_REASONS.loadingPolicies;
    }
    return options.hasTemplatesError
        ? SCHEDULE_CHANGE_POLICY_DISABLED_REASONS.policiesFailed
        : SCHEDULE_CHANGE_POLICY_DISABLED_REASONS.noPolicies;
}

export function partitionScheduleRowsForPolicyChange(
    rows: PolicyChangeRow[],
): SchedulePolicyChangePartition {
    const skippedById = new Map<string, ScheduleBulkSkippedRow>();
    const skip = (row: PolicyChangeRow, reason: string) => {
        const applicationId = getScheduleRowApplicationId(row);
        skippedById.set(applicationId, {
            applicationId,
            label: row.sourceLabel,
            reason,
        });
    };
    const bulkRowsByApplication = new Map<string, BulkScheduleRow>();
    for (const row of rows) {
        const blockReason = getPolicyChangeBlockReason(row);
        if (blockReason) {
            skip(row, blockReason);
            continue;
        }
        const applicationId = getScheduleRowApplicationId(row);
        if (bulkRowsByApplication.has(applicationId)) {
            continue;
        }
        const bulkRow = toBulkScheduleRow(row);
        if (!bulkRow) {
            skip(row, SCHEDULE_CHANGE_POLICY_SKIP_REASONS.noSource);
            continue;
        }
        bulkRowsByApplication.set(applicationId, bulkRow);
    }
    const bulkRows = [...bulkRowsByApplication.values()];
    return {
        rows: bulkRows,
        skipped: [...skippedById.values()],
        disabledReason:
            bulkRows.length === 0 && rows.length > 0
                ? SCHEDULE_CHANGE_POLICY_DISABLED_REASONS.noneEligible
                : undefined,
    };
}
